/**
 * CoachSequence — Type C. One anchored bubble, or a numbered run of them.
 *
 * Reuses the dismissal store from the existing CoachMark so a hint dismissed
 * either way stays dismissed. Adds what a sequence needs: step counter,
 * "Skip all", a spotlight ring on the anchor, and self-dismissal when the user
 * starts using the thing we were about to explain.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  dismissHint,
  isHintDismissed,
} from '@/components/vibe-kit/help/CoachMark';
import type { CoachStepCopy } from './overlayCopy';
import { useOverlaySlot } from './OverlayController';

type AnchorRef = { readonly current: HTMLElement | null };

export interface CoachSequenceStep extends CoachStepCopy {
  anchorRef: AnchorRef;
}

interface Props {
  /** Sequence id — used for the slot claim and the "skip all" record. */
  id: string;
  steps: CoachSequenceStep[];
  /** Gate the whole sequence (e.g. first visit only, feature available). */
  when?: boolean;
  /** Dim level behind the bubble, 0 disables the spotlight. */
  dim?: number;
  onComplete?: () => void;
}

const BUBBLE_W = 300;
const GAP = 12;

export const CoachSequence: React.FC<Props> = ({
  id,
  steps,
  when = true,
  dim = 0.45,
  onComplete,
}) => {
  const remaining = useMemo(
    () => steps.filter((s) => !isHintDismissed(s.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [finished, setFinished] = useState(remaining.length === 0);
  const reduceMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  const active = useOverlaySlot(id, 'coach', when && !finished);
  const step = remaining[index];

  // Measure the anchor, and keep measuring while the page moves.
  useEffect(() => {
    if (!active || !step) return undefined;
    let raf = 0;
    let tries = 0;
    const measure = () => {
      const el = step.anchorRef.current;
      if (!el || (el.getBoundingClientRect().width === 0 && tries < 30)) {
        tries += 1;
        raf = requestAnimationFrame(measure);
        return;
      }
      if (el) setRect(el.getBoundingClientRect());
    };
    raf = requestAnimationFrame(measure);
    const onMove = () => {
      const el = step.anchorRef.current;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [active, step]);

  // Action beats instruction: interacting with the anchor closes the step.
  useEffect(() => {
    if (!active || !step) return undefined;
    const el = step.anchorRef.current;
    if (!el) return undefined;
    const onUse = () => next();
    el.addEventListener('input', onUse, { once: true });
    el.addEventListener('click', onUse, { once: true });
    return () => {
      el.removeEventListener('input', onUse);
      el.removeEventListener('click', onUse);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step, index]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipAll();
      if (e.key === 'Enter') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  const next = () => {
    if (!step) return;
    dismissHint(step.id);
    if (index + 1 >= remaining.length) {
      setFinished(true);
      onComplete?.();
    } else {
      setIndex(index + 1);
    }
  };

  const skipAll = () => {
    remaining.forEach((s) => dismissHint(s.id));
    setFinished(true);
    onComplete?.();
  };

  if (!active || !step || !rect) return null;

  const pos = place(rect, step.placement ?? 'bottom');

  return (
    <>
      {dim > 0 && !reduceMotion.current && (
        <>
          <div
            className="fixed inset-0 z-[1040] transition-opacity"
            style={{ background: `rgba(15,18,28,${dim})` }}
            onClick={skipAll}
            role="presentation"
          />
          {/* Spotlight ring — a hole is faked with a ring, so the anchor stays clickable. */}
          <div
            className="pointer-events-none fixed z-[1045] rounded-lg ring-2 ring-primary"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0)',
              background: 'transparent',
            }}
          />
        </>
      )}

      <div
        className="fixed z-[1060] w-[300px] max-w-[calc(100vw-16px)] rounded-xl bg-navy p-3.5 shadow-2xl ring-1 ring-white/10 dark:bg-[#232734]"
        style={{ top: pos.top, left: pos.left }}
        role="dialog"
        aria-label={step.title}
      >
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-400" />
          <div className="min-w-0 flex-1">
            <h4 className="text-[12.5px] font-bold leading-snug text-white">
              {step.title}
            </h4>
            <p className="mt-1 text-[11.5px] leading-relaxed text-white/80">
              {step.body}
            </p>
          </div>
          <button
            type="button"
            onClick={skipAll}
            aria-label="Dismiss"
            className="flex-none rounded p-0.5 text-white/50 hover:bg-card/10 hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-3 flex items-center">
          {remaining.length > 1 && (
            <span className="text-[10.5px] text-white/45">
              {index + 1} of {remaining.length}
            </span>
          )}
          <div className="ml-auto flex gap-1.5">
            {remaining.length > 1 && (
              <button
                type="button"
                onClick={skipAll}
                className="px-2 py-1 text-[10.5px] text-white/60 hover:text-white"
              >
                Skip all
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="rounded-md bg-card/15 px-2.5 py-1 text-[10.5px] font-bold text-white hover:bg-card/25"
            >
              {index + 1 >= remaining.length ? 'Got it' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function place(
  rect: DOMRect,
  placement: 'top' | 'bottom' | 'left' | 'right'
): { top: number; left: number } {
  let top = 0;
  let left = 0;
  switch (placement) {
    case 'top':
      top = rect.top - GAP - 150;
      left = rect.left + rect.width / 2 - BUBBLE_W / 2;
      break;
    case 'right':
      top = rect.top;
      left = rect.right + GAP;
      break;
    case 'left':
      top = rect.top;
      left = rect.left - GAP - BUBBLE_W;
      break;
    default:
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - BUBBLE_W / 2;
  }
  left = Math.max(8, Math.min(left, window.innerWidth - BUBBLE_W - 8));
  top = Math.max(8, Math.min(top, window.innerHeight - 170));
  return { top, left };
}

export default CoachSequence;
